// src/api/ligaApi.js
const API_URL = process.env.REACT_APP_API_URL || "http://192.168.0.16:3001/api";
const SERVER_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace("/api", "")
  : "http://192.168.0.16:3001";

const getAuthHeaders = (isFormData = false) => {
  const token = localStorage.getItem("adminToken");
  const headers = {};
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Códigos típicos do Render free tier acordando do cold start (gateway/boot)
const STATUS_RETRYABLE = new Set([502, 503, 504]);

// fetch com backoff exponencial: encobre o cold start do Render (serviço
// hibernado pode levar ~30-50s para subir). Só re-tenta em 502/503/504 ou
// erro de rede — nunca em 2xx/4xx/500, que são respostas definitivas.
// Uso restrito a GETs idempotentes (não usar em POST/DELETE).
const fetchComRetry = async (url, options = {}, maxTentativas = 8) => {
  let delay = 1500;
  let ultimaResponse = null;
  let ultimoErro = null;
  for (let tentativa = 1; tentativa <= maxTentativas; tentativa++) {
    try {
      const response = await fetch(url, options);
      if (!STATUS_RETRYABLE.has(response.status)) return response;
      ultimaResponse = response; // serviço subindo: guarda e tenta de novo
      ultimoErro = null;
    } catch (err) {
      ultimoErro = err; // erro de rede: conexão recusada enquanto sobe
      ultimaResponse = null;
    }
    if (tentativa < maxTentativas) {
      await sleep(delay);
      delay = Math.min(Math.round(delay * 1.6), 10000);
    }
  }
  // Esgotou: devolve o último 503 (handleResponse formata a mensagem) ou relança
  if (ultimaResponse) return ultimaResponse;
  throw ultimoErro || new Error("Falha de conexão com o servidor.");
};

const handleResponse = async (response) => {
  if (response.status === 401 || response.status === 403) {
    localStorage.removeItem("adminToken");
    window.location.reload();
    throw new Error(
      "Sessão inválida ou expirada. Por favor, faça login novamente."
    );
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      message: `Erro de Servidor: Status ${response.status}`,
    }));
    throw new Error(
      err.message || err.error || "Ocorreu um erro desconhecido."
    );
  }
  if (response.status === 204) {
    return { success: true };
  }
  return response.json().catch(() => ({ success: true }));
};

export const getEscudoUrl = (originalUrl) => {
  if (!originalUrl) return "";
  if (originalUrl.startsWith("/uploads")) {
    return `${SERVER_BASE_URL}${originalUrl}`;
  }
  return `${API_URL}/image-proxy?url=${encodeURIComponent(originalUrl)}`;
};

export const loginAdmin = async (email, password) => {
  const response = await fetch(`${API_URL}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  return handleResponse(response);
};

export const getDadosCompletos = async () => {
  // Carga principal do site: retry encobre o cold start do Render
  const response = await fetchComRetry(`${API_URL}/dados-completos`);
  return handleResponse(response);
};

export const addEquipe = async (nome) => {
  const response = await fetch(`${API_URL}/equipes`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ nome }),
  });
  return handleResponse(response);
};

export const removeEquipe = async (id) => {
  const response = await fetch(`${API_URL}/equipes/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const updateTitulosEquipe = async (equipeId, quantidade) => {
  const response = await fetch(`${API_URL}/equipes/${equipeId}/titulos`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({ titulos: Number(quantidade) }),
  });
  return handleResponse(response);
};

export const addTime = async (timeData) => {
  const response = await fetch(`${API_URL}/times`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(timeData),
  });
  return handleResponse(response);
};

export const removeTime = async (id) => {
  const response = await fetch(`${API_URL}/times/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const searchCartolaTimes = async (query) => {
  if (!query) return [];
  const response = await fetch(
    `${API_URL}/cartola/search?q=${encodeURIComponent(query)}`
  );
  return handleResponse(response);
};

export const getEscalacao = async (cartolaTimeId, rodada) => {
  const params = new URLSearchParams();
  // rodada específica (ex.: ver uma rodada já fechada no Detalhes da Rodada)
  if (rodada != null) params.set("rodada", rodada);
  // Modo demo: se a página foi aberta com ?demo=1, repassa para o backend
  // simular uma rodada ao vivo com substituição do reserva de luxo
  const demo =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("demo") === "1";
  if (demo) params.set("demo", "1");
  const qs = params.toString();
  const response = await fetch(
    `${API_URL}/cartola/escalacao/${cartolaTimeId}${qs ? `?${qs}` : ""}`
  );
  return handleResponse(response);
};

export const getParciais = async () => {
  // Acompanha o cold start junto com a carga principal
  const response = await fetchComRetry(`${API_URL}/parciais`);
  return handleResponse(response);
};

export const fetchScoresForRound = async (numero) => {
  const response = await fetch(`${API_URL}/rodadas/${numero}/fetch-scores`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const updateAllTimes = async () => {
  const response = await fetch(`${API_URL}/times/update-all`, {
    method: "PUT",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const resetarPontuacoes = async () => {
  const response = await fetch(`${API_URL}/rodadas/reset`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const resetScoresForRound = async (numero) => {
  const response = await fetch(`${API_URL}/rodadas/${numero}/reset`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const getAdmins = async () => {
  const response = await fetch(`${API_URL}/admins`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const addAdmin = async (adminData) => {
  const response = await fetch(`${API_URL}/admins`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(adminData),
  });
  return handleResponse(response);
};

export const deleteAdmin = async (id) => {
  const response = await fetch(`${API_URL}/admins/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const setCaptain = async (timeId, equipeId) => {
  const response = await fetch(`${API_URL}/times/${timeId}/set-captain`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({ equipeId }),
  });
  return handleResponse(response);
};

export const uploadLogo = async (equipeId, formData) => {
  const response = await fetch(`${API_URL}/equipes/${equipeId}/logo`, {
    method: "POST",
    headers: getAuthHeaders(true),
    body: formData,
  });
  return handleResponse(response);
};
